import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, FilePlus2, CalendarClock, FileText, Settings, LogOut, Moon, Sun, BellRing, Scissors, Home, BarChart3, PawPrint } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { brandName } from "@/lib/brands";
import { ReminderBell } from "@/components/ReminderBell";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { to: "/clients", label: "Clients", icon: Users },
  { to: "/consultations", label: "Vet Consultation", icon: FilePlus2, roles: ["doctor", "admin", "staff"], hideBrands: ["kennelmart"] },
  { to: "/grooming", label: "Grooming", icon: Scissors, roles: ["staff", "admin"] },
  { to: "/boarding", label: "Boarding", icon: Home, roles: ["staff", "admin"] },
  { to: "/appointments", label: "Appointments", icon: CalendarClock },
  { to: "/reminders", label: "Reminders", icon: BellRing },
  { to: "/reports", label: "History", icon: FileText },
  { to: "/reports/business", label: "Business Reports", icon: BarChart3, roles: ["admin"] },
  { to: "/admin", label: "Admin", icon: Settings, roles: ["admin"] },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const visible = NAV.filter((n) => (!n.roles || n.roles.includes(user?.role)) && !(n.hideBrands && n.hideBrands.includes(user?.brand)));
  const scope = user?.role === "admin" ? "All Brands" : `${brandName(user?.brand)}${user?.branch ? ` · ${user.branch}` : ""}`;

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Sidebar */}
      <aside data-testid="sidebar" className="hidden md:flex md:w-64 lg:w-72 shrink-0 flex-col border-r border-border bg-card sticky top-0 h-screen overflow-y-auto">
        <div className="px-5 py-6">
          <div className="flex items-center justify-between gap-2.5">
            <div className="flex items-center gap-2.5 font-primary font-bold text-lg" data-testid="app-brand">
              <span className="grid place-items-center h-9 w-9 rounded-xl bg-primary text-primary-foreground shadow-sm"><PawPrint className="h-5 w-5" strokeWidth={1.75} /></span>
              Pet Care Suite
            </div>
            <ReminderBell />
          </div>
          <div data-testid="brand-scope" className="mt-4 rounded-xl border border-border/60 bg-secondary/60 px-3.5 py-2.5">
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Workspace</div>
            <div className="text-sm font-medium text-foreground mt-0.5">{scope}</div>
          </div>
        </div>
        <nav className="flex-1 px-3 space-y-1">
          {visible.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.exact}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all
                ${isActive
                  ? "bg-secondary text-foreground border-l-4 border-primary pl-2"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary/70 border-l-4 border-transparent pl-2"}`
              }
            >
              <item.icon className="h-4 w-4" strokeWidth={1.75} />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-border space-y-2">
          <div className="px-3 py-2">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Welcome</div>
            <div className="text-sm font-medium truncate" data-testid="welcome-name">{user?.name}</div>
            <div className="text-xs text-muted-foreground capitalize">{user?.role}</div>
          </div>
          <div className="flex items-center gap-2 px-1">
            <Button
              variant="outline" size="sm" className="flex-1" data-testid="theme-toggle-btn"
              onClick={toggle}
            >
              {theme === "dark" ? <Sun className="h-4 w-4 mr-2" /> : <Moon className="h-4 w-4 mr-2" />}
              {theme === "dark" ? "Light" : "Dark"}
            </Button>
            <Button
              variant="ghost" size="sm" data-testid="logout-btn"
              onClick={async () => { await logout(); navigate("/login"); }}
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 flex items-center justify-between px-4 py-3 border-b border-border bg-card">
        <div className="flex items-center gap-2 font-primary font-bold">
          <span className="grid place-items-center h-7 w-7 rounded-lg bg-primary text-primary-foreground"><PawPrint className="h-4 w-4" /></span>
          Pet Care Suite
        </div>
        <div className="flex items-center gap-1">
          <ReminderBell />
          <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle-mobile">
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
          <Button variant="ghost" size="icon" onClick={async () => { await logout(); navigate("/login"); }}>
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <div className="pt-16 md:pt-0">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
